import React, { Component } from 'react';
import moment from 'moment';
import { closeOutline as close, trashBin as trash, card, cubeOutline, arrowUndoOutline } from 'ionicons/icons';
import {
  IonItem,
  IonTitle,
  IonLabel,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonText,
  IonList,
  IonBackButton,
  IonModal,
  IonButton,
  IonIcon,
  withIonLifeCycle,
  IonFooter,
  IonGrid,
  IonRow,
  IonCol,
  IonBadge,
} from '@ionic/react';

import SystemResource from '../resources/System';
import HeaderButtons from '../components/HeaderButtons';
import ReservationsService from '../services/reservations';
import EventReservationService from '../services/eventReservation';
import RentalReservationService from '../services/rentalReservations';

const STATUS_COLORS = {
  awaiting_payment: 'tertiary',
  pending: 'warning',
  confirmed: 'success',
  cancelled: 'danger',
};

const STATUS_LABELS = {
  awaiting_payment: 'In attesa di pagamento',
  pending: 'In attesa',
  confirmed: 'Confermato',
  cancelled: 'Cancellato',
};

class ReservationsPage extends Component {
  constructor(props) {
    super(props);

    this.loader = React.createRef();

    this.state = {
      reservations: [],
      eventReservations: [],
      rentalReservations: [],
      isModalOpen: false,
      selectedService: null,
      isCancellable: false,
      deleteModalIsOpen: false,
    };
  }

  async ionViewWillEnter() {
    await this.getReservations();
  }

  async getReservations() {
    if (this.props.loader?.current?.show) {
      this.props.loader.current.show();
    }

    try {
      SystemResource.checkVersion();
      const [serviceRes, eventRes, rentalRes] = await Promise.all([
        ReservationsService.all(),
        EventReservationService.all(),
        RentalReservationService.all(),
      ]);

      await this.setState({
        reservations: serviceRes.data || serviceRes,
        eventReservations: eventRes.data || eventRes,
        rentalReservations: rentalRes.data || rentalRes,
      });
    } catch (error) {
      if (error.response?.status !== 401) console.error(error);
    } finally {
      if (this.props.loader?.current?.dismiss) {
        this.props.loader.current.dismiss();
      }
    }
  }

  checkCancellationAvailability(reservation) {
    if (reservation._type === 'rental') {
      const cancelAdvanceDays = reservation.cancel_advance_days ?? 0;
      const cancellable =
        ['pending', 'confirmed'].includes(reservation.status) &&
        moment()
          .startOf('day')
          .isSameOrBefore(moment(reservation.start_day).startOf('day').subtract(cancelAdvanceDays, 'days'));
      this.setState({ isCancellable: cancellable });
      return;
    }

    if (reservation._type === 'event') {
      const reservationDate = moment(reservation.start_day);
      const now = moment().startOf('day');
      const maxCancellationDays = reservation.eventService?.booking_cancellations || 0;
      const cancellationLimit = reservationDate.clone().subtract(maxCancellationDays, 'days');
      const isCancellable = now.isSameOrBefore(cancellationLimit);

      this.setState({ isCancellable });
      return;
    }

    const reservationDate = moment(reservation.start_day);
    const now = moment().startOf('day');
    const maxCancellationDays = reservation.service?.booking_cancellations || 0;
    const cancellationLimit = reservationDate.clone().subtract(maxCancellationDays, 'days');
    const isCancellable =
      ['awaiting_payment', 'pending', 'confirmed'].includes(reservation.status) &&
      now.isSameOrBefore(cancellationLimit);

    this.setState({ isCancellable });
  }

  async submitHandler(that, id) {
    try {
      if (that.state.selectedService?._type === 'rental') {
        await RentalReservationService.cancel(id);
      } else if (that.state.selectedService?._type === 'event') {
        await EventReservationService.remove(id);
      } else {
        await ReservationsService.remove(id);
      }
      await that.props.toast.current.add({ message: `Prenotaziona cancellata correttamente.` });

      await that.setState({ selectedService: null });
      await that.setState({ isModalOpen: false });

      await that.props.history.replace('/app/reservations');
    } catch (e) {
      const serverMessage = e.response?.data?.error;

      that.props.toast.current.add({
        message: serverMessage || `Problema nella cancellazione della prenotazione.`,
        color: 'danger',
      });

      delete that.props.toast.current.onDidDismiss;
    } finally {
      await this.getReservations();
    }
    this.setState({ deleteModalIsOpen: false });
  }

  renderServiceItem(reservation, i) {
    const date = moment(reservation.start_day).clone().format('DD/MM/YY');
    const start = moment(reservation.start_day).format('HH:mm');

    return (
      <IonItem
        key={`s-${reservation.id}`}
        color={i % 2 === 0 ? 'light' : undefined}
        onClick={() => {
          this.setState({ isModalOpen: true, selectedService: { ...reservation, _type: 'service' } });
          this.checkCancellationAvailability({ ...reservation, _type: 'service' });
        }}
      >
        <IonText>
          <h4>{reservation.service ? reservation.service.name : reservation.shop_service?.name}</h4>
          <p>
            {date} - {start}
          </p>

          {/*
            Prenotazione combinata: il servizio include il noleggio di un dispositivo.
            Le due prenotazioni si presentano come una entità sola e si annullano
            insieme, dall'endpoint del servizio.
          */}
          {reservation.rental && (
            <p className="ion-no-margin">
              <IonIcon icon={cubeOutline} /> Include il noleggio di {reservation.rental.device_name || 'un dispositivo'}
              {reservation.rental.end_day
                ? ` fino al ${moment(reservation.rental.end_day).format('DD/MM/YY HH:mm')}`
                : ''}
            </p>
          )}

          {/*
            La riconsegna è l'appuntamento in cui il dispositivo rientra in
            farmacia. Vive dentro questa scheda e non come voce a sé: la
            prenotazione è una, e si annulla da qui.
          */}
          {reservation.return && (
            <p className="ion-no-margin">
              <IonIcon icon={arrowUndoOutline} /> Riconsegna il{' '}
              {moment(reservation.return.start_day).format('DD/MM/YY [alle] HH:mm')}
            </p>
          )}
        </IonText>

        <IonBadge slot="end" color={STATUS_COLORS[reservation.status] || 'medium'}>
          {STATUS_LABELS[reservation.status] || reservation.status}
        </IonBadge>
      </IonItem>
    );
  }

  renderEventItem(reservation, i) {
    const date = moment(reservation.start_day).clone().format('DD/MM/YY');
    const start = moment(reservation.start_day).format('HH:mm');

    return (
      <IonItem
        key={`e-${reservation.id}`}
        color={i % 2 === 0 ? 'light' : undefined}
        onClick={() => {
          this.setState({ isModalOpen: true, selectedService: { ...reservation, _type: 'event' } });
          this.checkCancellationAvailability({ ...reservation, _type: 'event' });
        }}
      >
        <IonText>
          <h4>
            {reservation.eventService?.name || 'Servizio evento'}
            {reservation.event ? ` — ${reservation.event.name}` : ''}
          </h4>

          <p>
            {date} - {start}
          </p>
        </IonText>
      </IonItem>
    );
  }

  renderRentalItem(reservation, i) {
    const startDate = moment(reservation.start_day).format('DD/MM/YY');
    const endDate = reservation.end_day ? moment(reservation.end_day).format('DD/MM/YY') : '';
    const start = (reservation.start_appointment || '').slice(0, 5);

    return (
      <IonItem
        key={`r-${reservation.id}`}
        color={i % 2 === 0 ? 'light' : undefined}
        onClick={() => {
          this.setState({ isModalOpen: true, selectedService: { ...reservation, _type: 'rental' } });
          this.checkCancellationAvailability({ ...reservation, _type: 'rental' });
        }}
      >
        <IonText>
          <h4>{reservation.device_name || 'Dispositivo'}</h4>
          <p>
            {startDate}
            {endDate ? ` → ${endDate}` : ''} - {start}
          </p>
        </IonText>

        <IonBadge slot="end" color={STATUS_COLORS[reservation.status] || 'medium'}>
          {STATUS_LABELS[reservation.status] || reservation.status}
        </IonBadge>
      </IonItem>
    );
  }

  renderModalContent() {
    const { selectedService, isCancellable } = this.state;
    if (!selectedService) return null;

    const isRental = selectedService._type === 'rental';
    const isEvent = selectedService._type === 'event';

    return (
      <IonContent fullscreen>
        {isRental ? (
          <>
            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Dettaglio Noleggio</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Dispositivo:</h4>
                  <p>{selectedService.device_name || 'Dispositivo'}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Data inizio:</h4>
                  <p>{moment(selectedService.start_day).format('dddd | DD MMMM YYYY')}</p>
                </IonLabel>
              </IonItem>

              {selectedService.end_day && (
                <IonItem lines="none">
                  <IonLabel>
                    <h4 className="ion-text-uppercase">Data fine:</h4>
                    <p>{moment(selectedService.end_day).format('dddd | DD MMMM YYYY')}</p>
                  </IonLabel>
                </IonItem>
              )}

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Ora ritiro:</h4>
                  <p>{(selectedService.start_appointment || '').slice(0, 5)}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Stato:</h4>
                  <p>
                    {selectedService.status === 'awaiting_payment'
                      ? 'In attesa di pagamento'
                      : selectedService.status === 'pending'
                        ? 'In attesa di conferma'
                        : selectedService.status === 'confirmed'
                          ? 'Confermato'
                          : selectedService.status === 'cancelled'
                            ? 'Cancellata'
                            : selectedService.status}
                  </p>
                </IonLabel>
              </IonItem>
            </ion-item-group>

            {selectedService.note && (
              <ion-item-group>
                <ion-item-divider>
                  <IonLabel className="ion-text-uppercase" color="dark">
                    <b>Note</b>
                  </IonLabel>
                </ion-item-divider>

                <IonItem lines="none">
                  <IonLabel>
                    <p dangerouslySetInnerHTML={{ __html: selectedService.note }}></p>
                  </IonLabel>
                </IonItem>
              </ion-item-group>
            )}

            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Azione</b>
                </IonLabel>
              </ion-item-divider>

              {selectedService.status === 'awaiting_payment' && (
                <IonItem lines="none">
                  <IonLabel>
                    <IonButton
                      expand="full"
                      color="success"
                      onClick={() => {
                        this.setState({ isModalOpen: false });
                        this.props.history.push(`/app/reservations/${selectedService.id}/payment`);
                      }}
                    >
                      <IonIcon slot="start" icon={card} /> Completa il pagamento
                    </IonButton>
                  </IonLabel>
                </IonItem>
              )}

              <IonItem lines="none">
                <IonLabel>
                  {isCancellable ? (
                    <IonButton expand="full" color="danger" onClick={() => this.setState({ deleteModalIsOpen: true })}>
                      <IonIcon slot="start" icon={trash} /> Cancella prenotazione
                    </IonButton>
                  ) : (
                    selectedService.status !== 'cancelled' &&
                    moment(selectedService.start_day).startOf('day').isSameOrAfter(moment().startOf('day')) && (
                      <IonItem className="ion-text-wrap">Non è più possibile cancellare questa prenotazione</IonItem>
                    )
                  )}
                </IonLabel>
              </IonItem>
            </ion-item-group>
          </>
        ) : isEvent ? (
          <>
            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Prenotazione Evento</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Evento:</h4>
                  <p>{selectedService.event?.name || '-'}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Servizio:</h4>
                  <p>{selectedService.eventService?.name || '-'}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Giorno:</h4>
                  <p>{moment(selectedService.start_day).format('dddd | DD MMMM YYYY')}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Ora inizio:</h4>
                  <p>{moment(selectedService.start_day).format('HH:mm')}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Ora fine:</h4>
                  <p>{moment(selectedService.end_day).format('HH:mm')}</p>
                </IonLabel>
              </IonItem>

              {selectedService.eventService?.duration && (
                <IonItem>
                  <IonLabel>
                    <h4 className="ion-text-uppercase">Durata:</h4>
                    <p>{selectedService.eventService.duration} min</p>
                  </IonLabel>
                </IonItem>
              )}

              {selectedService.eventService?.price !== undefined && (
                <IonItem lines="none">
                  <IonLabel>
                    <h4 className="ion-text-uppercase">Prezzo:</h4>
                    <p>{selectedService.eventService.price} €</p>
                  </IonLabel>
                </IonItem>
              )}
            </ion-item-group>

            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Note</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Note al farmacista:</h4>
                  {selectedService.note ? (
                    <p dangerouslySetInnerHTML={{ __html: selectedService.note }}></p>
                  ) : (
                    <p>Nessuna nota</p>
                  )}
                </IonLabel>
              </IonItem>
            </ion-item-group>

            {!selectedService._readonly && (
              <ion-item-group>
                <ion-item-divider>
                  <IonLabel className="ion-text-uppercase" color="dark">
                    <b>Azione</b>
                  </IonLabel>
                </ion-item-divider>

                <IonItem lines="none">
                  <IonLabel>
                    {isCancellable ? (
                      <IonButton
                        id="delete"
                        expand="full"
                        color="danger"
                        onClick={() => this.setState({ deleteModalIsOpen: true })}
                      >
                        <IonIcon slot="start" icon={trash} />
                        Cancella prenotazione
                      </IonButton>
                    ) : (
                      <IonItem className="ion-text-wrap">Non è più possibile cancellare questa prenotazione</IonItem>
                    )}
                  </IonLabel>
                </IonItem>
              </ion-item-group>
            )}
          </>
        ) : (
          <>
            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Prenotazione</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Servizio:</h4>
                  <p>{selectedService.service?.name}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Giorno:</h4>
                  <p>{moment(selectedService.start_day).format('dddd | DD MMMM YYYY')}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Ora inizio:</h4>
                  <p>{moment(selectedService.start_day).format('HH:mm')}</p>
                </IonLabel>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Ora fine:</h4>
                  <p>{moment(selectedService.end_day).format('HH:mm')}</p>
                </IonLabel>
              </IonItem>

              {selectedService.service?.duration && (
                <IonItem>
                  <IonLabel>
                    <h4 className="ion-text-uppercase">Durata:</h4>
                    <p>{selectedService.service.duration} min</p>
                  </IonLabel>
                </IonItem>
              )}

              {selectedService.service?.price !== undefined && (
                <IonItem lines="none">
                  <IonLabel>
                    <h4 className="ion-text-uppercase">Prezzo:</h4>
                    <p>{selectedService.service.price} €</p>
                  </IonLabel>
                </IonItem>
              )}

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Stato:</h4>
                  <p>{STATUS_LABELS[selectedService.status] || selectedService.status}</p>
                </IonLabel>
              </IonItem>
            </ion-item-group>

            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Note</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  <h4 className="ion-text-uppercase">Note al farmacista:</h4>
                  {selectedService.note ? (
                    <p dangerouslySetInnerHTML={{ __html: selectedService.note }}></p>
                  ) : (
                    <p>Nessuna nota</p>
                  )}
                </IonLabel>
              </IonItem>
            </ion-item-group>

            <ion-item-group>
              <ion-item-divider>
                <IonLabel className="ion-text-uppercase" color="dark">
                  <b>Azione</b>
                </IonLabel>
              </ion-item-divider>

              <IonItem lines="none">
                <IonLabel>
                  {isCancellable ? (
                    <IonButton
                      id="delete"
                      expand="full"
                      color="danger"
                      onClick={() => this.setState({ deleteModalIsOpen: true })}
                    >
                      <IonIcon slot="start" icon={trash} />
                      Cancella prenotazione
                    </IonButton>
                  ) : selectedService.status === 'cancelled' ? (
                    <IonItem className="ion-text-wrap">Prenotazione annullata</IonItem>
                  ) : (
                    <IonItem className="ion-text-wrap">Non è più possibile cancellare questa prenotazione</IonItem>
                  )}
                </IonLabel>
              </IonItem>
            </ion-item-group>
          </>
        )}
      </IonContent>
    );
  }

  render() {
    const { reservations, eventReservations, rentalReservations, isModalOpen, selectedService, deleteModalIsOpen } =
      this.state;

    return (
      <>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/app/home" />
            </IonButtons>

            <HeaderButtons {...this.props} />
            <IonTitle>Prenotazioni</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent fullscreen>
          <IonList>
            <ion-list-header>
              <IonLabel className="ion-text-uppercase">
                <b>Prenotazioni Servizi</b>
              </IonLabel>
              <IonButton slot="end" fill="clear" size="small" onClick={() => this.props.history.push('/app/services')}>
                Vedi servizi
              </IonButton>
            </ion-list-header>

            {reservations[0] ? (
              <>
                {reservations.map((reservation, i) => this.renderServiceItem(reservation, i))}
                <IonItem lines="none">
                  <IonButton expand="block" fill="clear" onClick={() => this.props.history.push('/app/services')}>
                    Prenota un servizio
                  </IonButton>
                </IonItem>
              </>
            ) : (
              <IonItem>
                <IonLabel>Nessuna prenotazione servizi</IonLabel>
                <IonButton
                  slot="end"
                  fill="clear"
                  size="small"
                  onClick={() => this.props.history.push('/app/services')}
                >
                  Vedi servizi
                </IonButton>
              </IonItem>
            )}

            <ion-list-header>
              <IonLabel className="ion-text-uppercase">
                <b>Prenotazioni Eventi</b>
              </IonLabel>
              <IonButton slot="end" fill="clear" size="small" onClick={() => this.props.history.push('/app/events')}>
                Vedi eventi
              </IonButton>
            </ion-list-header>

            {eventReservations[0] ? (
              <>
                {eventReservations.map((reservation, i) => this.renderEventItem(reservation, i))}
                <IonItem lines="none">
                  <IonButton expand="block" fill="clear" onClick={() => this.props.history.push('/app/events')}>
                    Vedi eventi disponibili
                  </IonButton>
                </IonItem>
              </>
            ) : (
              <IonItem>
                <IonLabel>Nessuna prenotazione eventi</IonLabel>
                <IonButton slot="end" fill="clear" size="small" onClick={() => this.props.history.push('/app/events')}>
                  Vedi eventi
                </IonButton>
              </IonItem>
            )}

            <ion-list-header>
              <IonLabel className="ion-text-uppercase">
                <b>Prenotazioni Noleggio</b>
              </IonLabel>
              <IonButton slot="end" fill="clear" size="small" onClick={() => this.props.history.push('/app/rentals')}>
                Vedi noleggio
              </IonButton>
            </ion-list-header>

            {rentalReservations[0] ? (
              <>
                {rentalReservations.map((reservation, i) => this.renderRentalItem(reservation, i))}
                <IonItem lines="none">
                  <IonButton expand="block" fill="clear" onClick={() => this.props.history.push('/app/rentals')}>
                    Noleggia un dispositivo
                  </IonButton>
                </IonItem>
              </>
            ) : (
              <IonItem>
                <IonLabel>Nessuna prenotazione noleggio</IonLabel>
                <IonButton slot="end" fill="clear" size="small" onClick={() => this.props.history.push('/app/rentals')}>
                  Vedi noleggio
                </IonButton>
              </IonItem>
            )}
          </IonList>

          {isModalOpen && (
            <IonModal
              isOpen={isModalOpen}
              onDidDismiss={() => this.setState({ isModalOpen: false, selectedService: null })}
            >
              <IonHeader>
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton
                      onClick={() => {
                        this.setState({ isModalOpen: false, selectedService: null });
                      }}
                    >
                      <IonIcon icon={close} />
                    </IonButton>
                  </IonButtons>
                  <IonTitle>Riepilogo</IonTitle>
                </IonToolbar>
              </IonHeader>

              {this.renderModalContent()}
            </IonModal>
          )}

          {deleteModalIsOpen && (
            <IonModal isOpen={deleteModalIsOpen} onDidDismiss={() => this.setState({ deleteModalIsOpen: false })}>
              <IonHeader>
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton onClick={() => this.setState({ deleteModalIsOpen: false })}>
                      <IonIcon icon={close} />
                    </IonButton>
                  </IonButtons>

                  <IonTitle>Eliminare la prenotazione?</IonTitle>
                </IonToolbar>
              </IonHeader>

              <IonContent>
                <IonItem>
                  <IonText>Vuoi davvero eliminare la prenotazione?</IonText>
                </IonItem>

                <IonItem>
                  <IonText>
                    <b>Attenzione quest'azione è irreversibile</b>
                  </IonText>
                </IonItem>
              </IonContent>

              <IonFooter className="ion-padding">
                <IonToolbar>
                  <IonGrid>
                    <IonRow>
                      <IonCol className="ion-text-right">
                        <IonButton
                          expand="block"
                          color="danger"
                          onClick={() => this.submitHandler(this, selectedService?.id)}
                          className="ion-margin-horizontal"
                        >
                          <IonIcon slot="start" icon={trash} /> Elimina prenotazione
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonToolbar>
              </IonFooter>
            </IonModal>
          )}
        </IonContent>
      </>
    );
  }
}

export default withIonLifeCycle(ReservationsPage);
